import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Send, Plus, MessageSquare, Sparkles } from "lucide-react";

export default function Chat() {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => setSessions((await api.get("/chat/sessions")).data);

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [current?.messages?.length]);

  const send = async () => {
    if (!input.trim()) return;
    const msg = input;
    setInput("");
    setLoading(true);
    const optimistic = {
      ...(current || { messages: [], title: msg.slice(0,60) }),
      messages: [...(current?.messages || []), { role: "user", content: msg }],
    };
    setCurrent(optimistic);
    try {
      const r = await api.post("/chat/message", { message: msg, session_id: current?.id });
      const updated = {
        ...(current || { title: msg.slice(0,60) }),
        id: r.data.session_id,
        messages: [...optimistic.messages, { role: "assistant", content: r.data.reply }],
      };
      setCurrent(updated);
      loadSessions();
    } catch (e) {
      // rollback
      setCurrent(current);
    } finally { setLoading(false); }
  };

  const newChat = () => { setCurrent(null); setInput(""); };
  const openSession = async (sid) => {
    const r = await api.get(`/chat/sessions/${sid}`);
    setCurrent(r.data);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-160px)]" data-testid="chat-page">
      <div className="card-flat p-4 overflow-auto">
        <button onClick={newChat} className="btn-gold w-full flex items-center justify-center gap-2 mb-4" data-testid="new-chat"><Plus size={12} /> Yeni Sohbet</button>
        <div className="label-caps mb-2">Geçmiş</div>
        {sessions.length === 0 && <div className="text-xs text-neutral-500">Henüz sohbet yok</div>}
        {sessions.map(s => (
          <button key={s.id} onClick={()=>openSession(s.id)} className={`w-full text-left px-3 py-2 text-xs border-l-2 ${current?.id===s.id?"border-[--gold] bg-white/[.03]":"border-transparent hover:bg-white/[.02]"}`} style={current?.id===s.id?{borderLeftColor:"var(--gold)"}:{}} data-testid={`session-${s.id}`}>
            <div className="truncate">{s.title}</div>
            <div className="label-caps mt-1">{new Date(s.updated_at).toLocaleDateString("tr-TR")}</div>
          </button>
        ))}
      </div>

      <div className="lg:col-span-3 card-flat flex flex-col">
        <div className="p-6 border-b border-white/5 flex items-center gap-3">
          <Sparkles size={20} style={{color:"var(--gold)"}} />
          <div>
            <div className="label-caps">AI Kuyumcu Asistanı</div>
            <div className="font-display text-xl">Uzman Danışman</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-auto p-6 space-y-4">
          {!current && (
            <div className="text-center py-12">
              <MessageSquare size={40} className="mx-auto text-neutral-600 mb-4" />
              <div className="font-display text-2xl mb-2">Sohbete Başla</div>
              <div className="text-sm text-neutral-500 max-w-md mx-auto">Altın ayarları, işçilik hesabı, hurda değerleme veya yatırım stratejisi hakkında soru sor.</div>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {["22 ayar bileziği 14 ayarla nasıl karşılaştırırım?","Bugün altın al mı sat mı?","Hurda alımında dikkat edilecekler?"].map(q => (
                  <button key={q} onClick={()=>setInput(q)} className="border border-white/10 px-3 py-2 text-xs hover:border-[--gold]">{q}</button>
                ))}
              </div>
            </div>
          )}
          {current?.messages?.map((m, i) => (
            <div key={i} className={`flex ${m.role==="user"?"justify-end":"justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role==="user"?"bg-white/[.05] border border-white/10":""}`} style={m.role==="assistant"?{background:"var(--surface-2)",borderLeft:"2px solid var(--gold)"}:{}}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="px-4 py-3 text-sm text-neutral-400" style={{background:"var(--surface-2)",borderLeft:"2px solid var(--gold)"}}>
                <span className="animate-pulse">Düşünüyor…</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/5">
          <div className="flex gap-2">
            <input value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&send()} placeholder="Sorunuzu yazın..." className="flex-1 bg-transparent border border-white/10 px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="chat-input" />
            <button onClick={send} disabled={loading||!input.trim()} className="btn-primary-gold flex items-center gap-2" data-testid="chat-send"><Send size={12} /> Gönder</button>
          </div>
        </div>
      </div>
    </div>
  );
}
